import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function AspireTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Dynamic Angled Header */}
          <div className={`relative bg-gradient-to-br ${colors.headerBg} ${colors.headerText} p-8 pb-16`}>
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-white" style={{ clipPath: 'polygon(0 50%, 100% 0, 100% 100%, 0 100%)' }}></div>
            <h1 className={`text-5xl font-bold mb-3 relative z-10 ${colors.sectionTitle}`}>{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className="text-xl mb-4 relative z-10">{personalInfo.email}</div>
            <div className="flex flex-wrap gap-4 text-sm relative z-10">
              {personalInfo.phone && <span>{personalInfo.phone}</span>}
              {personalInfo.address && <span>{personalInfo.address}</span>}
              {personalInfo.website && <span>{personalInfo.website}</span>}
            </div>
          </div>

          <div className="px-8 pt-4 pb-8">
            {/* Summary with Accent */}
            {summary && (
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className={`w-12 h-1 ${colors.accentBg} mr-3`}></div>
                  <h2 className={`text-lg font-bold ${colors.sectionTitle} uppercase tracking-wide`}>{labels.professionalSummary}</h2>
                </div>
                <p className="text-gray-700 leading-relaxed pl-15">{summary}</p>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className={`w-12 h-1 ${colors.accentBg} mr-3`}></div>
                  <h2 className={`text-lg font-bold ${colors.sectionTitle} uppercase tracking-wide`}>{labels.workExperience}</h2>
                </div>
                <div className="space-y-4 pl-15">
                  {experience.map((exp: any) => (
                    <div key={exp.id}>
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className={`text-lg font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                        <span className="text-sm text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                      </div>
                      <div className={`text-base font-semibold ${colors.accentText} mb-2`}>{exp.company}</div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills in Colorful Boxes */}
            {skills && skills.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className={`w-12 h-1 ${colors.accentBg} mr-3`}></div>
                  <h2 className={`text-lg font-bold ${colors.sectionTitle} uppercase tracking-wide`}>{labels.skills}</h2>
                </div>
                <div className="grid grid-cols-2 gap-4 pl-15">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx}>
                      <div className={`text-sm font-bold ${colors.accentText} mb-2`}>{skillGroup.category}</div>
                      <div className="flex flex-wrap gap-2">
                        {skillGroup.skills?.map((skill: string, i: number) => (
                          <span key={i} className={`text-xs ${colors.badgeBg} ${colors.badgeText} px-3 py-1 rounded-full`}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {education && education.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className={`w-12 h-1 ${colors.accentBg} mr-3`}></div>
                  <h2 className={`text-lg font-bold ${colors.sectionTitle} uppercase tracking-wide`}>{labels.education}</h2>
                </div>
                <div className="space-y-3 pl-15">
                  {education.map((edu: any) => (
                    <div key={edu.id}>
                      <div className={`text-base font-bold ${colors.sectionTitle}`}>{edu.degree}</div>
                      <div className={`text-sm ${colors.accentText}`}>{edu.institution}</div>
                      <div className="text-sm text-gray-600">{edu.graduationDate}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
